/**
 * "No tick for N s while the market is open" (launch blocker CB3): a chart or DOM ladder whose
 * symbol has had no market-data update (trade, quote or book) for `STALE_AFTER_MS` while the CME
 * session is open shows a stale badge — the feed may be frozen even though every socket looks
 * connected. Suppressed while the market is closed (weekend, the daily 16:00–17:00 CT break),
 * and for `STALE_AFTER_MS` after it opens, so a quiet open is not flagged.
 *
 * Pure and timer-driven (no DOM): `node --test` runs it with fake timers.
 */
import { marketPhase } from "../bars/levels.ts";

/** Conservative: a CME front month quotes many times a second while open. */
export const STALE_AFTER_MS = 15_000;

/** How often a watcher re-checks. */
export const STALE_CHECK_MS = 1_000;

/**
 * True when nothing arrived for `thresholdMs` (since `lastUpdateMs`, or since watching began when
 * nothing arrived yet) and the market has been open for that whole span's end: open now and
 * `thresholdMs` ago.
 */
export function isFeedStale(
  lastUpdateMs: number | null,
  watchingSinceMs: number,
  nowMs: number,
  thresholdMs = STALE_AFTER_MS,
): boolean {
  const ref = lastUpdateMs ?? watchingSinceMs;
  if (nowMs - ref < thresholdMs) return false;
  return marketPhase(nowMs) !== "closed" && marketPhase(nowMs - thresholdMs) !== "closed";
}

export type StaleWatchOptions = {
  onChange: (stale: boolean) => void;
  thresholdMs?: number;
  checkMs?: number;
  now?: () => number;
};

/**
 * Watches one symbol's feed: `touch()` on every update, `onChange` when the stale verdict flips.
 * `start()` begins the periodic check (and counts from now if nothing arrived yet).
 */
export class StaleWatch {
  private last: number | null = null;
  private since = 0;
  private stale = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly threshold: number;
  private readonly checkMs: number;
  private readonly now: () => number;
  private readonly opts: StaleWatchOptions;

  constructor(opts: StaleWatchOptions) {
    this.opts = opts;
    this.threshold = opts.thresholdMs ?? STALE_AFTER_MS;
    this.checkMs = opts.checkMs ?? STALE_CHECK_MS;
    this.now = opts.now ?? (() => Date.now());
  }

  start(): void {
    if (this.timer !== null) return;
    this.since = this.now();
    this.timer = setInterval(() => {
      this.check();
    }, this.checkMs);
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.set(false);
  }

  /** A market-data update for the symbol arrived (at `atMs`, default now). */
  touch(atMs: number = this.now()): void {
    this.last = this.last === null ? atMs : Math.max(this.last, atMs);
    this.check();
  }

  isStale(): boolean {
    return this.stale;
  }

  check(): void {
    this.set(isFeedStale(this.last, this.since, this.now(), this.threshold));
  }

  private set(stale: boolean): void {
    if (stale === this.stale) return;
    this.stale = stale;
    this.opts.onChange(stale);
  }
}

/** Badge text: "No data for 23 s" (whole seconds since the last update, or since watching). */
export function staleLabel(lastUpdateMs: number | null, watchingSinceMs: number, nowMs: number): string {
  const s = Math.max(0, Math.floor((nowMs - (lastUpdateMs ?? watchingSinceMs)) / 1000));
  return s >= 120 ? `No data for ${String(Math.floor(s / 60))} min` : `No data for ${String(s)} s`;
}
